import React, { useMemo } from 'react';
import { LightTable, LightTableColumDef } from '../../../theme/components';
import {
  ItemsWorkspaceRequestWrapped,
  getWorkspaceQuotaUsageSelector,
} from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import { getPolyglot } from '../../../i18n';
import moment from 'moment';
import {
  getTextStatusFromRequest,
  getTypeStatusFromRequest,
} from '../util/getStatusFromRequest';
import DetailsIconButton from '../../../theme/icons/DetailsIconButton';
import { Link as LinkRouter } from 'react-router-dom';
import { LabelState } from '../../../theme/components';
import { useAppSelector } from '../../../redux/store.model';
import { getUserSelector } from '../../../redux/users/api/User.selectors';
import { arrayToRecord } from '../../../util/ArrayUtil';
import { IUser } from '../../../redux/users/api/user.model';
import { isNotNullOrUndefined } from '../../../util/types/Array';
import { Box, Link, Skeleton, styled } from '@mui/material';
import { StoerkIdUsageAPIResponse } from '../../../redux/stoerkID/StoerkIdAPI.model';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';

export interface CustomersTableProps {
  items: ItemsWorkspaceRequestWrapped[];
}

/**
 * Determine if a workspace is a Supplier (Organization) based on subworkspaces data.
 * Returns 'organization' | 'simple' | undefined
 */
export function getWorkspaceType(
  workspaceId: string | undefined,
  subworkspacesData: Record<string, any> | undefined
): 'organization' | 'simple' | undefined {
  if (!workspaceId) return undefined;
  if (!subworkspacesData) return undefined;
  return Object.keys(subworkspacesData).length > 0 ? 'organization' : 'simple';
}

function CustomersTable(props: CustomersTableProps) {
  const { items } = props;
  const polyglot = getPolyglot();
  const usersDicSerialized = useAppSelector((state) =>
    JSON.stringify(
      arrayToRecord<IUser, IUser>(
        items
          .map((request) => {
            const user =
              request.workspace?.users[0] || request.lastRequest?.creatorId;
            return user
              ? (getUserSelector(user)(state).data as IUser)
              : undefined;
          })
          .filter(isNotNullOrUndefined),
        'id'
      )
    )
  );
  const usersDic: Record<string, IUser> = useMemo(
    () => JSON.parse(usersDicSerialized),
    [usersDicSerialized]
  );

  const quotaDicSerialized = useAppSelector((state) =>
    JSON.stringify(
      arrayToRecord<any, any>(
        items
          .map((request) => {
            const workspaceId = request.workspace?.id;
            return workspaceId
              ? ({
                  id: workspaceId,
                  ...getWorkspaceQuotaUsageSelector(workspaceId)(state).data,
                } as StoerkIdUsageAPIResponse & { id: string })
              : undefined;
          })
          .filter(isNotNullOrUndefined),
        'id'
      )
    )
  );
  const quotaDic: Record<
    string,
    (StoerkIdUsageAPIResponse & { id: string }) | undefined
  > = useMemo(() => JSON.parse(quotaDicSerialized), [quotaDicSerialized]);

  // Fetch subworkspaces for each workspace to determine type
  const subworkspacesDicSerialized = useAppSelector((state) =>
    JSON.stringify(
      arrayToRecord<any, any>(
        items
          .map((request) => {
            const workspaceId = request.workspace?.id;
            if (!workspaceId) return undefined;
            const subData =
              StoerkIdAPI.endpoints.getSubWorkspaces.select(workspaceId)(state);
            return { id: workspaceId, data: subData.data };
          })
          .filter(isNotNullOrUndefined),
        'id'
      )
    )
  );
  const subworkspacesDic = useMemo(
    () => JSON.parse(subworkspacesDicSerialized),
    [subworkspacesDicSerialized]
  );

  // Compute total usage including child workspaces (like on the details page)
  // Uses shallowEqual via JSON serialization to prevent unnecessary re-renders
  const totalUsageSerialized = useAppSelector((state) => {
    const dic: Record<string, { commanders: number; users: number }> = {};
    items.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (!workspaceId) return;
      const ownUsage = getWorkspaceQuotaUsageSelector(workspaceId)(state).data;
      let commanders = ownUsage?.commanders?.length ?? 0;
      let users = ownUsage?.users?.length ?? 0;
      const subData =
        StoerkIdAPI.endpoints.getSubWorkspaces.select(workspaceId)(state).data;
      if (subData) {
        Object.keys(subData).forEach((childId) => {
          const childUsage =
            StoerkIdAPI.endpoints.getWorkspaceUsage.select(childId)(state).data;
          commanders += childUsage?.commanders?.length ?? 0;
          users += childUsage?.users?.length ?? 0;
        });
      }
      dic[workspaceId] = { commanders, users };
    });
    return JSON.stringify(dic);
  });
  const totalUsageDic: Record<string, { commanders: number; users: number }> =
    useMemo(() => JSON.parse(totalUsageSerialized), [totalUsageSerialized]);

  // Track which supplier workspaces still have child usage loading
  const loadingUsageSetSerialized = useAppSelector((state) => {
    const loading: string[] = [];
    items.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (!workspaceId) return;
      const subData =
        StoerkIdAPI.endpoints.getSubWorkspaces.select(workspaceId)(state).data;
      if (subData && Object.keys(subData).length > 0) {
        const anyChildLoading = Object.keys(subData).some((childId) => {
          const result =
            StoerkIdAPI.endpoints.getWorkspaceUsage.select(childId)(state);
          return result.isLoading || result.isUninitialized;
        });
        if (anyChildLoading) loading.push(workspaceId);
      }
    });
    return JSON.stringify(loading);
  });
  const loadingUsageSet: Set<string> = useMemo(
    () => new Set(JSON.parse(loadingUsageSetSerialized)),
    [loadingUsageSetSerialized]
  );

  const columns: LightTableColumDef[] = useMemo(
    () => [
      // {
      //   field: 'id',
      //   headerName: 'Reference',
      //   orderable: true,
      // },
      {
        field: 'workspaceName',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_workspace_name'
        ),
        orderable: true,
      },
      {
        field: 'type',
        headerName: polyglot.t('stoerk_id.customer_list.header_cell_type'),
        orderable: true,
      },
      {
        field: 'email',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_billing_email'
        ),
        orderable: true,
        TableCellProps: {
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 220,
          },
        },
      },
      {
        field: 'stripeId',
        headerName: polyglot.t(
          'stoerk_id.detail_page.status.stripe_id_header_cell'
        ),
        orderable: true,
      },
      {
        field: 'lastRequestDate',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_last_request_date'
        ),
        orderable: true,
      },
      {
        field: 'lastRequestStatus',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_last_request_status'
        ),
        orderable: true,
      },
      {
        field: 'userQuota',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_billing_users'
        ),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      {
        field: 'deviceQuota',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_billing_commanders'
        ),
        orderable: true,
        TableCellProps: { align: 'right' },
      },

      {
        field: 'detail',
        headerName: polyglot.t('stoerk_id.customer_list.header_cell_customer'),
        TableCellProps: { align: 'center', width: '80px' },
      },
    ],
    [polyglot]
  );

  const rows = useMemo(
    () =>
      items.map((item, i) => {
        const name = item.lastRequest?.workspaceName ?? item.workspace?.name;
        const url = item.workspace?.id
          ? `/account/id/${item.workspace.id}`
          : `/account/user/${item.creatorId}`;
        const email =
          usersDic[item.workspace?.users[0] as any]?.email ??
          usersDic[item.creatorId as any]?.email ??
          item.billingEmail ??
          item.workspace?.users[0] ??
          '';

        const workspaceType = getWorkspaceType(
          item.workspace?.id,
          subworkspacesDic[item.workspace?.id ?? '']?.data
        );

        return {
          // use as key
          id: `${item.id}-${i}`,
          workspaceName: {
            value: name,
            element:
              item.creatorId || item.workspace?.id ? (
                <Link to={url} component={LinkRouter}>
                  {name}
                </Link>
              ) : (
                name
              ),
          },
          type: {
            value: workspaceType ?? '',
            element: workspaceType
              ? polyglot.t(`stoerk_id.customer_list.type_${workspaceType}`)
              : '',
          },
          stripeId: (item.workspace as any)?.stripeId ?? item.stripeUserId,
          email:
            usersDic[item.workspace?.users[0] as any]?.email ??
            usersDic[item.creatorId as any]?.email ??
            item.billingEmail ??
            item.workspace?.users[0] ??
            '',
          lastRequestDate: {
            value: item.lastRequest?.created ?? -Infinity,
            element: item.lastRequest?.created
              ? moment(item.lastRequest?.created).format('L')
              : '',
          },
          userQuota: {
            value: totalUsageDic[item?.workspace?.id ?? '']?.users ?? 0,
            element: loadingUsageSet.has(item?.workspace?.id ?? '') ? (
              <Skeleton width={40} />
            ) : (
              totalUsageDic[item?.workspace?.id ?? '']?.users ?? ''
            ),
          },
          deviceQuota: {
            value: totalUsageDic[item?.workspace?.id ?? '']?.commanders ?? 0,
            element: loadingUsageSet.has(item?.workspace?.id ?? '') ? (
              <Skeleton width={40} />
            ) : (
              totalUsageDic[item?.workspace?.id ?? '']?.commanders ?? ''
            ),
          },
          lastRequestStatus: {
            value: item.requestStatus,
            element: (
              <LabelState
                variant={
                  item.lastRequest
                    ? getTypeStatusFromRequest(item.lastRequest)
                    : 'info'
                }
              >
                {item.lastRequest
                  ? polyglot.t(getTextStatusFromRequest(item.lastRequest))
                  : polyglot.t('stoerk_id.detail_page.request.no_request')}
              </LabelState>
            ),
          },
          detail:
            item.creatorId || item.workspace?.id ? (
              <LinkRouter to={url}>
                <DetailsIconButton />
              </LinkRouter>
            ) : null,
        };
      }),
    [
      items,
      usersDic,
      quotaDic,
      subworkspacesDic,
      totalUsageDic,
      loadingUsageSet,
      polyglot,
    ]
  );

  return (
    <LightTable
      columns={columns}
      rows={rows}
      variant="clear"
      TableContainerComponent={TableContainerComponent}
      orderByDefault="lastRequestDate"
    />
  );
}

const TableContainerComponent = styled(Box)(({ theme }) => ({}));
export default CustomersTable;
